import { z } from 'zod';

/** Request/response contracts for the HTTP API. The OpenAPI document (mobile client) is generated from these. */

export const Platform = z.enum(['web', 'android', 'ios']);
export const DeviceInfo = z.object({ label: z.string().min(1).max(80), platform: Platform });
export type DeviceInfo = z.infer<typeof DeviceInfo>;

export const UserDto = z.object({
  id: z.string(), email: z.string().email(), name: z.string().nullable(), avatarUrl: z.string().nullable(),
  emailVerified: z.boolean(), createdAt: z.string(),
});
export type UserDto = z.infer<typeof UserDto>;

export const DeviceDto = z.object({
  id: z.string(), label: z.string(), platform: Platform, lastSeenAt: z.string(), current: z.boolean(),
});
export type DeviceDto = z.infer<typeof DeviceDto>;

/**
 * Mirrors the WorkOS password policy (dashboard: Authentication › Email + Password): 8+ characters with an upper-case
 * letter, a lower-case letter, a digit and a symbol. The dashboard also demands a zxcvbn score of 2, which only the
 * web can pre-check; WorkOS enforces it and the API reports it as `weak_password`. Change the two together.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_CLASSES = [
  { id: 'upper', label: 'An upper-case letter', pattern: /[A-Z]/ },
  { id: 'lower', label: 'A lower-case letter', pattern: /[a-z]/ },
  { id: 'digit', label: 'A number', pattern: /\d/ },
  { id: 'symbol', label: 'A symbol', pattern: /[^A-Za-z0-9]/ },
] as const;
export const Password = PASSWORD_CLASSES.reduce<z.ZodString>(
  (schema, c) => schema.regex(c.pattern, `${c.label} is required`),
  z.string().min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`).max(256),
);

export const RegisterRequest = z.object({ email: z.string().email(), password: Password, name: z.string().min(1).max(80).optional(), device: DeviceInfo });
export const PasswordLoginRequest = z.object({ email: z.string().email(), password: z.string().min(1), device: DeviceInfo });
/** Password sign-up and sign-in stop at the email step until the address is confirmed: the API answers 202 with this, mails a six-digit code, and `/v1/auth/verify` turns both into a session. */
export const PendingVerification = z.object({ pendingToken: z.string(), email: z.string().email() });
export type PendingVerification = z.infer<typeof PendingVerification>;
export const VerifyEmailRequest = z.object({ pendingToken: z.string().min(10), code: z.string().regex(/^\d{6}$/, 'Six digits'), device: DeviceInfo });
export const RefreshRequest = z.object({ refreshToken: z.string().min(20).optional() });
export const ResetRequest = z.object({ email: z.string().email() });
export const ResetConfirmRequest = z.object({ token: z.string().min(10), newPassword: Password });
export const ChangePasswordRequest = z.object({ newPassword: Password });
/** `avatarUrl` is a square data URL the client has already resized; null clears it. Capped so D1 rows stay small. */
export const UpdateAccountRequest = z.object({ name: z.string().min(1).max(80).optional(), avatarUrl: z.string().max(200_000).nullable().optional() });

/** Web clients get the refresh token as an HttpOnly cookie, so `refreshToken` is only present for native platforms. */
export const AuthResponse = z.object({ user: UserDto, accessToken: z.string(), expiresIn: z.number().int(), refreshToken: z.string().optional(), deviceId: z.string() });
export type AuthResponse = z.infer<typeof AuthResponse>;

/**
 * Settings › About › Send feedback (TW-8). Works signed out, like the timer. `context` is shown to the person before
 * it is sent and carries nothing about their sessions, projects or tasks.
 */
export const FeedbackCategory = z.enum(['bug', 'idea', 'question']);
export type FeedbackCategory = z.infer<typeof FeedbackCategory>;
export const FeedbackRequest = z.object({
  message: z.string().trim().min(1).max(4000),
  category: FeedbackCategory.optional(),
  /** Where to reply, when signed out. Signed in, the account's address is used and this is ignored. */
  email: z.string().trim().email().max(254).optional(),
  context: z.object({ version: z.string().max(40), platform: z.string().max(120), theme: z.string().max(20), signedIn: z.boolean() }),
});
export type FeedbackRequest = z.infer<typeof FeedbackRequest>;

export const ErrorResponse = z.object({ error: z.string(), message: z.string().optional() });
export const OkResponse = z.object({ ok: z.literal(true) });
